<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    isConfigurationForm
  />
  <form @submit.prevent="save">
    <OperationFormLayout
      v-if="!loading"
      :id="$route.params.shaftId"
      :formDisabled="disable"
      :formSaving="saving"
      isConfigurationForm
      operationType="shaft"
      @cancel="back"
    >
      <!-- Details section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('operations.details_description')"
        :sectionTitle="$t('operations.details_title')"
      >
        <FormRow
          :errors="errors?.shaft?.name"
          :label="$t('operations.operation_name_label_shaft')"
          labelFor="shaft_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': errors?.shaft?.name }">
              <input
                id="shaft_name"
                v-model="shaft.name"
                @input="validateInput('name')"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('operations.operation_division_label')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <DivisionSelect
              v-model="shaft.division"
              :divisions="divisions"
            />
          </div>
        </FormRow>
        <FormRow
          v-if="shaft.division && shaft.division.isNew"
          :errors="divisionNameErrors"
          :label="$t('operations.operation_new_division_label')"
          labelFor="new_division_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': divisionNameErrors }">
              <input
                id="new_division_name"
                v-model="shaft.division.name"
              >
            </div>
          </div>
        </FormRow>
      </OperationFormSection>

      <!-- Dimensions section -->
      <NoticeMessage
        v-if="warningText"
        class="configuration-notice"
        colorStyle="warning"
        iconStyle="warning"
        :message="warningText"
      />

      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('shafts.configuration.shaft.type.description')"
        :sectionTitle="$t('shafts.configuration.shaft.type.title')"
      >
        <div>
          <FormRow
            :errors="errors?.shaft?.shape && !shaft?.shape ? errors?.shaft?.shape : []"
            :label="$t('shafts.select_shaft')"
            labelFor="shaft_shape"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input form-row__input--no-peek">
              <ShaftShape
                id="shaft_shape"
                v-model="shaft.shape"
                :class="{ 'shape--error': errors?.shaft?.shape && !shaft?.shape }"
                :iconList="shapeIcons"
              />
            </div>
          </FormRow>
        </div>
      </OperationFormSection>

      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('operations.shaft_detail_description')"
        :sectionTitle="$t('operations.dimensions_title')"
      >
        <template v-if="shaft?.dimensions">
          <FormRow
            v-if="shaft.shape === 'circle'"
            :errors="errors?.shaft?.diameter"
            :label="$t('shafts.configuration.shaft.dimensions.diameter')"
            labelFor="diameter"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input form-row__input--no-peek">
              <div
                :class="{ input: true, 'input--error': errors?.shaft?.diameter }"
              >
                <input
                  id="diameter"
                  v-model="shaft.dimensions.diameter"
                  @input="validateInput('diameter')"
                >
              </div>
            </div>
          </FormRow>
          <template v-else>
            <FormRow
              :errors="errors?.shaft?.width"
              :label="$t('shafts.configuration.shaft.dimensions.width')"
              labelFor="width"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div
                  :class="{ input: true, 'input--error': errors?.shaft?.width }"
                >
                  <input
                    id="width"
                    v-model="shaft.dimensions.width"
                    @input="validateInput('width')"
                  >
                </div>
              </div>
            </FormRow>

            <FormRow
              :errors="errors?.shaft?.length"
              :label="$t('shafts.configuration.shaft.dimensions.length')"
              labelFor="length"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div
                  :class="{ input: true, 'input--error': errors?.shaft?.length }"
                >
                  <input
                    id="length"
                    v-model="shaft.dimensions.length"
                    @input="validateInput('length')"
                  >
                </div>
              </div>
            </FormRow>
          </template>
        </template>

        <FormRow
          :errors="errors?.shaft?.set_spacing"
          :label="$t('shafts.set_spacing')"
          labelFor="set_spacing"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div
              :class="{
                input: true,
                'input--error': errors?.shaft?.set_spacing,
              }"
            >
              <input
                id="set_spacing"
                v-model="shaft.set_spacing"
                @input="validateInput('set_spacing')"
              >
            </div>
          </div>
        </FormRow>

        <FormRow
          :errors="errors?.shaft?.number_of_sets"
          :label="$t('shafts.number_of_sets')"
          labelFor="number_of_sets"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div
              :class="{ input: true, 'input--error': errors?.shaft?.number_of_sets }"
            >
              <input
                id="number_of_sets"
                v-model="shaft.number_of_sets"
                @input="validateInput('number_of_sets')"
              >
            </div>
          </div>
        </FormRow>
      </OperationFormSection>

      <!-- Configure elements section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('shafts.configuration.shaft.configure_elements_description')"
        :sectionTitle="$t('shafts.issues.shaft_elements')"
      >
        <router-link
          :aria-label="$t('nav.definition')"
          :to="{ name: 'shaftV2Definition' }"
        >
          <div class="diagram-container">
            <ConfigureElementsSVG />
            <button
              class="button button--margin-right button--transparent button--border"
              type="button"
            >
              <PencilSVG class="icon button__icon" />
              <span class="button__label">
                {{ $t('shafts.configuration.shaft.configure_elements') }}
              </span>
            </button>
          </div>
        </router-link>
      </OperationFormSection>

      <!-- HME section -->
      <OperationFormSection
        v-if="shaft.super_user"
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('shafts.configuration.shaft.hme_add_ons_description')"
        :sectionTitle="$t('shafts.configuration.shaft.hme_add_ons')"
      >
        <div class="hme__section hme__section--light">
          <Checkbox
            id="hme_toggle"
            v-model="shaft.heavy_mobile_equipment_enabled"
            :modifiers="['switch']"
          >
            <p class="typo-small-title">
              {{ $t('shafts.configuration.shaft.hme_add_ons_label') }}
            </p>
          </Checkbox>
        </div>
      </OperationFormSection>
    </OperationFormLayout>
  </form>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import dig from '@/functions/dig';
import useAjax from '@/functions/use_ajax';
import omitObjectItem from '@/functions/omit_object_item';
import { useRoute, useRouter } from 'vue-router';
import isDesktop from '@/functions/responsive';

// Components
import FormRow from '@/components/form/row.vue';
import DivisionSelect from '@/components/operations/division_select.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import Checkbox from '@/components/form/checkbox.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';
import ShaftShape from '@/components/form/shaft_shape.vue';

// Svg
import ConfigureElementsSVG from '@/images/configure-elements.svg';
import PencilSVG from '@/images/pencil-24.svg';

const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const divisions = ref(null);
const initialShaft = ref(null);
const initialShaftDimensions = ref(createNewShaft().dimensions);
const shaft = reactive(createNewShaft());
const errors = ref(null);
const saving = ref(false);
const loading = ref(false);
const disable = ref(true);
const warningText = ref(null);

/**
 * Shape list
 */
const shapeIcons = ref(
  [
    'circle',
    'rectangle',
  ]
);

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${route.params.organisationId}/divisions`,
  shaft: `/organisations/${route.params.organisationId}/shafts_v2/shafts/${route.params.shaftId}`,
};

function displayWarning({ dimensions, set_spacing, number_of_sets }) {
  const dimensionValidation = {
    diameter: false,
    width: false,
    length: false,
    setSpacing: false
  };

  if (shaft.shape === 'circle') {
    dimensionValidation.diameter = Number(dimensions.diameter) !== Number(initialShaftDimensions.value.diameter);
  } else {
    dimensionValidation.width = Number(dimensions.width) !== Number(initialShaftDimensions.value.width);
    dimensionValidation.length = Number(dimensions.length) !== Number(initialShaftDimensions.value.length);
  }

  dimensionValidation.setSpacing = Number(set_spacing) !== Number(initialShaft.value.set_spacing);
  let numOfSetsChanged = Number(number_of_sets) !== Number(initialShaft.value.number_of_sets);

  const warningTranslations = I18n.t('shafts.configuration.shaft.warning_text');

  if (Object.values(dimensionValidation).indexOf(true) > -1) {
    return warningTranslations.diameter_and_spacing;
  }

  if (numOfSetsChanged) {
    return warningTranslations.number_of_sets;
  }

  return null;
}

/**
 * Form validation, check if the form was updated,
 * if form is updated enable the save button
 */
watch(() => shaft, (newValue) => {
    const formFields = [
      'name',
      'division',
      'division_id',
      'diameter',
      'width',
      'length',
      'set_spacing',
      'number_of_sets',
      'heavy_mobile_equipment_enabled'
    ];

    warningText.value = displayWarning(newValue);

    const changedFields = formFields.filter((field) => {
      // Check if the dimensions were changed
      if (shaft.shape === 'circle' && ['diameter'].includes(field) || ['width', 'length'].includes(field)) {
        return Number(newValue.dimensions[field]) && Number(newValue.dimensions[field]) !== Number(initialShaftDimensions.value[field]);
      }
      // Check if a division was set (if it was then a division object will exist)
      if (newValue[field] && typeof newValue[field] === 'object' && !initialShaft.value[field]) {
        return newValue[field]?.id !== initialShaft.value['division_id'];
      }
      // Compare division objects
      if (newValue[field] && typeof newValue[field] === 'object') {
        return (JSON.stringify(newValue[field]) !== JSON.stringify(initialShaft.value[field]));
      }
      // Compare other string instances
      if (!['diameter', 'width', 'length', 'division', 'division_id'].includes(field)) {
        return newValue[field].toString() !== initialShaft.value[field].toString();
      }
    });
    disable.value = changedFields.length <= 0;
  },
  { deep: true }
);

onMounted(() => {
  loadDivisions();
  loadShaft();
});

const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);

async function loadShaft() {
  if (route.params.shaftId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.shaft);

    if (ok) {
      initialShaft.value = data;
      initialShaftDimensions.value = { ...initialShaftDimensions.value, ...data.dimensions };
      Object.assign(shaft, data);
      loading.value = false;
    }
  }
}

async function loadDivisions() {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
}

function createNewShaft() {
  return {
    name: '',
    division: null,
    set_spacing: null,
    number_of_sets: null,
    shape: 'circle',
    dimensions: {
      diameter: null,
      width: null,
      length: null,
    },
  };
}

function formatDivisionAttributes() {
  if (shaft.division?.isNew) {
    return {
      division_id: 'new',
      new_division: shaft.division.name,
    };
  } else {
    return {
      division_id: shaft.division?.id || null,
    };
  }
}

/**
 * Update hme variable only if super_user
 */
function updateHme() {
  if (shaft?.super_user) {
    return { heavy_mobile_equipment_enabled: shaft.heavy_mobile_equipment_enabled };
  }
}

const formatShaftForPost = () => {
  let shaftDimensions = null;
  switch (true) {
    case shaft.shape === 'circle':
      shaftDimensions = omitObjectItem(['width', 'length'], shaft.dimensions);
      break;

    default:
      shaftDimensions = omitObjectItem(['diameter'], shaft.dimensions);
      break;
  }

  return {
    shaft: {
      name: shaft.name,
      shape: shaft.shape,
      dimensions: shaftDimensions,
      set_spacing: shaft.set_spacing,
      number_of_sets: shaft.number_of_sets,
      ...formatDivisionAttributes(),
      ...updateHme()
    },
  };
};

async function save() {
  if (!route.params.shaftId) return;

  errors.value = null;
  saving.value = true;

  let { ok, data } = await ajax(ENDPOINTS.shaft, {
    method: 'PATCH',
    body: formatShaftForPost(),
  });

  if (ok) {
    back();
  } else {
    errors.value = data;
  }

  saving.value = false;
}

function validateInput(field) {
  if (errors.value) {
    errors.value.shaft[field] = null;
  }
}

function back() {
  router.replace({
    name: isDesktop() ? 'shaftV2' : 'shaftV2Configuration',
    params: { shaftId: route.params.shaftId },
  });
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.diagram-container {
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: map-get($background-color, darkest);
  border-radius: map-get($border-radius, heavier);

  .button {
    position: absolute;
    margin: auto;
  }
}

.hme {
  &__section {
    padding: bu(2);
    border-radius: map-get($border-radius, heavier);

    &--light {
      background-color: map-get($background-color, light);
    }
  }
}

.configuration-notice {
  margin: bu(2) bu(5) 0 bu(5);
}
</style>
